import React, { useContext } from 'react';
import Link from 'next/link';
import { AppRoutes } from 'src/lib/routes';
import SubscribeNewsletter from './SubscribeNewsletter';
import { BsFacebook, BsInstagram } from 'react-icons/bs';
import FooterImg15 from 'public/images/illu/illu_15.png';
import FooterImg16 from 'public/images/illu/illu_16.png';
import FooterImg17 from 'public/images/illu/illu_17.png';
import FooterImg18 from 'public/images/illu/illu_18.png';
import FooterImg19 from 'public/images/illu/illu_19.png';
import ImageWithFallback from './ImageWithFallback';
import { ControlHeaderPageNavContext } from 'src/contexts/ControlHeaderPageNavProvider';
import useInitialData from 'src/hooks/useInitialData';
import { sortBy } from 'lodash-es';
import { useTranslations } from 'next-intl';
import LocaleSelector from 'src/components/LocaleSelector';

const Footer: React.FC = () => {
  const { showFooter } = useContext(ControlHeaderPageNavContext)
  const year = new Date().getFullYear();
  const { countries } = useInitialData();
  const translation = useTranslations('components.Footer');

  return showFooter ? (
    <div className="tw-bg-brand-green tw-py-16 lg:tw-py-24">
      <div className="container tw-mb-8">
        <div className="tw-flex tw-justify-between tw-items-end">
          <div className="tw-flex-shrink-0 tw-w-18 lg:tw-w-40">
            <ImageWithFallback src={FooterImg15} className="tw-static tw-block tw-w-full tw-h-auto tw-object-cover" alt="" />
          </div>
          <div className="tw-flex-shrink-0 tw-w-44 tw-hidden lg:tw-block">
            <ImageWithFallback src={FooterImg16} className="tw-static tw-block tw-w-full tw-h-auto tw-object-cover" alt="" />
          </div>
          <div className="tw-flex-shrink-0 tw-w-24 lg:tw-w-44">
            <ImageWithFallback src={FooterImg17} className="tw-static tw-block tw-w-full tw-h-auto tw-object-cover" alt="" />
          </div>
          <div className="tw-flex-shrink-0 tw-w-16 lg:tw-w-32">
            <ImageWithFallback src={FooterImg18} className="tw-static tw-block tw-w-full tw-h-auto tw-object-cover" alt="" />
          </div>
          <div className="tw-flex-shrink-0 tw-w-40 tw-hidden lg:tw-block">
            <ImageWithFallback src={FooterImg19} className="tw-static tw-block tw-w-full tw-h-auto tw-object-cover" alt="" />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-lg-4 tw-text-center lg:tw-text-left">
            <div className="tw-text-4xl tw-font-styleSans tw-font-semibold tw-mb-2 lg:tw-mb-4 tw-leading-none">{translation('soSeekSophie')}</div>
            <div className="tw-text-xs md:tw-text-base">{translation('paragraph1')}</div>

            <div className="tw-my-8 tw-border-brand-navy tw-border-t lg:tw-hidden"></div>
          </div>
          <div className="offset-lg-1 col-lg-7">
            <div className="row tw-mb-8">
              <div className="col-6 col-lg-3 tw-mb-4 lg:tw-mb-0">
                <div className="tw-text-xl tw-font-semibold tw-font-styleSans tw-leading-none tw-mb-2">{translation('destinations')}</div>
                {sortBy(countries, [(o) => -o.optionsCount]).map((country)=> (
                  <div key={country.id} className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                    <a aria-label={country.name} href={AppRoutes.countryRoute({ id: country.slug }).toPath()} className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none">
                      {country.name}
                    </a>
                  </div>
                ))}
              </div>

              <div className="col-6 col-lg-3 tw-mb-4 lg:tw-mb-0">
                <div className="tw-text-xl tw-font-semibold tw-font-styleSans tw-leading-none tw-mb-2">{translation('aboutUs')}</div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.aboutRoute().toPath()}>
                    <a aria-label="Our Story" className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none">
                      {translation('ourStory')}
                    </a>
                  </Link>
                </div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.ourMissionRoute().toPath()}>
                    <a
                      aria-label="Travel as a Force for Good"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('ourMission')}
                    </a>
                  </Link>
                </div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.ourCurationRoute().toPath()}>
                    <a
                      aria-label="Curation Process"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('curationProcess')}
                    </a>
                  </Link>
                </div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.sustainabilityRoute().toPath()}>
                    <a
                      aria-label="Sustainability"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('sustainability')}
                    </a>
                  </Link>
                </div>
                <div className="tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.customerReviewsRoute().toPath()}>
                    <a
                      aria-label="Reviews"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('review')}
                    </a>
                  </Link>
                </div>
              </div>

              <div className="col-6 col-lg-3 tw-mb-4 lg:tw-mb-0">
                <div className="tw-text-xl tw-font-semibold tw-font-styleSans tw-mb-2 tw-leading-none">{translation('discoverMore')}</div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.corporateRoute().toPath()}>
                    <a aria-label="Corporate" className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none">
                      {translation('corporateExperience')}
                    </a>
                  </Link>
                </div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.inviteRoute().toPath()}>
                    <a
                      aria-label="Invite a friend"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('inviteFriend')}
                    </a>
                  </Link>
                </div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.promotionsRoute().toPath()}>
                    <a
                      aria-label="Current Promotions"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('currentPromotion')}
                    </a>
                  </Link>
                </div>
              </div>

              <div className="col-6 col-lg-3">
                <div className="tw-text-xl tw-font-semibold tw-font-styleSans tw-mb-2 tw-leading-none">{translation('getInTouch')}</div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.contactRoute().toPath()}>
                    <a aria-label="Contact Us" className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none">
                      {translation('contactUs')}
                    </a>
                  </Link>
                </div>
                <div className="tw-mb-1 lg:tw-mb-2 tw-leading-none">
                  <Link legacyBehavior href={AppRoutes.ambassadorHomeRoute().toPath()}>
                    <a
                      aria-label="Be a Seek Sophie Ambassador"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('ambassador')}
                    </a>
                  </Link>
                </div>
                <div className="tw-leading-none">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.creatorsCommunityRoute().toPath()}>
                    <a
                      aria-label="Join Seek Sophie Creators"
                      className="hover:tw-underline tw-text-brand-navy tw-text-xs md:tw-text-base tw-leading-none"
                    >
                      {translation('joinSeeksophie')}
                    </a>
                  </Link>
                </div>
              </div>
            </div>
            <div className="tw-my-8 tw-border-brand-navy tw-border-t lg:tw-hidden"></div>
            <div>
              <div className="tw-mb-4 lg:tw-mb-8 tw-text-center lg:tw-text-left">
                <div className="tw-mb-4 tw-block lg:tw-hidden">
                  <div className="tw-flex tw-justify-center tw-items-center">
                    <a
                      href="https://www.facebook.com/seeksophie"
                      className="tw-mr-2"
                      aria-label="Follow us on Facebook"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsFacebook size={28} className="tw-text-brand-navy" />
                    </a>
                    <a
                      href="https://www.instagram.com/seeksophie/"
                      aria-label="Follow us on Instagram"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <div className="tw-h-7 tw-w-7 tw-rounded-full tw-bg-brand-navy tw-p-1.5 tw-text-center">
                        <BsInstagram size={16} className="tw-text-brand-green tw-leading-7 tw-align-top" />
                      </div>
                    </a>
                  </div>
                </div>
                <div className="tw-text-lg lg:tw-text-xl tw-font-styleSans tw-font-semibold tw-mb-2 tw-leading-none">{translation('letBeFriend')}</div>
                <div className="tw-text-xs md:tw-text-base tw-leading-none">
                  {translation('getTheScoop')}
                </div>
              </div>

              <div className="tw-flex tw-justify-center lg:tw-justify-start tw-items-start">
                <div className='md:tw-w-88'><SubscribeNewsletter /></div>
                <div className="tw-hidden tw-flex-1 lg:tw-flex lg:tw-items-center lg:tw-justify-between tw-ml-7 tw-pt-2">
                  <div className="tw-flex tw-justify-center tw-items-center">
                    <a
                      href="https://www.facebook.com/seeksophie"
                      className="tw-mr-2"
                      aria-label="Follow us on Facebook"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsFacebook size={28} className="tw-text-brand-navy" />
                    </a>
                    <a
                      href="https://www.instagram.com/seeksophie/"
                      aria-label="Follow us on Instagram"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <div className="tw-h-7 tw-w-7 tw-rounded-full tw-bg-brand-navy tw-p-1.5 tw-text-center">
                        <BsInstagram size={16} className="tw-text-brand-green tw-leading-7 tw-align-top" />
                      </div>
                    </a>
                  </div>
                  <LocaleSelector showLanguageName={true} />
                </div>
              </div>
              <div className="tw-flex tw-justify-center tw-my-8 tw-block lg:tw-hidden">
                <LocaleSelector showLanguageName={true} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container tw-mt-4 lg:tw-mt-0">
        <div className="tw-flex tw-justify-center lg:tw-justify-start">
          <span className="tw-text-sm tw-font-semibold">&#169; Seek Sophie {year}</span>
          <Link legacyBehavior prefetch={false} href={AppRoutes.privacyRoute().toPath()} aria-label="Privacy Policy">
            <a className="hover:tw-underline tw-text-brand-navy tw-text-sm tw-font-semibold tw-ml-4">{translation('privacy')}</a>
          </Link>
          <Link legacyBehavior prefetch={false} href={AppRoutes.termsRoute().toPath()} aria-label="Term of Conditions">
            <a className="hover:tw-underline tw-text-brand-navy tw-text-sm tw-font-semibold tw-ml-4">{translation('terms')}</a>
          </Link>
        </div>
        <div className='tw-text-xs tw-text-center md:tw-text-left tw-mt-1'>{translation('travelAgentLicence')} TA03435</div>
        <div className="tw-text-inherit"></div>
      </div>
    </div>
  ) : null;
};

export default Footer;
